import React, { useState, useEffect } from 'react';
import { firestore } from '../../firebase/config';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import VisitasChart from './VisitasChart';
import EditModal from './EditModal';

// --- Função Auxiliar para Normalização ---
const normalizarTexto = (texto) => {
    if (!texto) return '';
    return String(texto).trim().toUpperCase();
};

// Lista de Localidades (Padronizada)
const OPCOES_LOCALIDADES = [
    "1 - Alamar", "2 - Alegria", "3 - Alto da Boa Vista", "4 - Antares", "5 - Arivaldo Reis",
    "6 - Assentamento Santa Maria", "7 - Cajueiro", "8 - Catarito", "9 - Centauro", "10 - Centro",
    "11 - Distrito da Colônia", "12 - Dinah Borges", "13 - Doutor Gusmão", "14 - Edgar Trancoso",
    "15 - Encanto das Águas", "16 - Fazenda São José", "17 - Gabiarra", "18 - Itapuã",
    "19 - Ivan Moura", "20 - Jardim Amendoeira", "21 - Jardins de Eunápolis", "22 - Juca Rosa",
    "23 - Matinha", "24 - Minas Gerais", "25 - Moisés Reis", "26 - MOTOR", "27 - Mundo Novo",
    "28 - Nova Eunápolis", "29 - Parque Colonial", "30 - Parque das Framboesas", "31 - Parque Nacional",
    "32 - Parque Renovação", "33 - Pequi", "34 - P. Maravilha", "35 - Ponto Bahia",
    "36 - Ponto Maneca (Colônia III)", "37 - Projeto Produzir", "38 - Quilometro 52",
    "39 - Recanto das Arvores", "40 - Residencial Alecrim", "41 - Residencial Arnaldo Moura",
    "42 - Residencial Talismã", "43 - Roça do Povo", "44 - Rosa Neto", "45 - Santa Isabel",
    "46 - Santa Lúcia", "47 - Santa Rita", "48 - Sapucaeira", "49 - Stela Reis",
    "50 - Tiago de Melo I (Alecrim I)", "51 - Tiago de Melo II (Alecrim II)", "52 - Urbis I",
    "53 - Urbis II", "54 - Urbis III", "55 - Veracell", "56 - Vista Alegre",
    "57 - Vivendas Costa Azul"
];

function DashboardDiario({ user }) {
    const [visitas, setVisitas] = useState([]);
    const [filteredVisitas, setFilteredVisitas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedVisit, setSelectedVisit] = useState(null);
    const [filterAgente, setFilterAgente] = useState('');
    const [filterQuarteirao, setFilterQuarteirao] = useState('');
    const [filterLocalidade, setFilterLocalidade] = useState('');
    const [agentsMap, setAgentsMap] = useState({});

    // ==============================
    // 🔹 Buscar agentes uma vez
    // ==============================
    useEffect(() => {
        const fetchAgents = async () => {
            try {
                const q = query(collection(firestore, "usuarios"), where("perfil", "==", "agente"));
                const querySnapshot = await getDocs(q);
                const agents = {};

                querySnapshot.docs.forEach(doc => {
                    const data = doc.data();
                    if (data.email) {
                        agents[data.email.toLowerCase()] = data.nome || data.email;
                    }
                    agents[doc.id] = data.nome || data.email;
                });

                setAgentsMap(agents);
            } catch (error) {
                console.error("Erro ao buscar agentes: ", error);
            }
        };
        fetchAgents();
    }, []);

    // ==============================
    // 🔹 Buscar visitas diárias (Manual)
    // ==============================
    const fetchVisitas = async () => {
        setLoading(true);
        try {
            const startOfDay = new Date();
            startOfDay.setHours(0, 0, 0, 0);
            const endOfDay = new Date();
            endOfDay.setHours(23, 59, 59, 999);

            const qVisitas = query(
                collection(firestore, 'relatoriosDiarios_FAD1'),
                where('dataAtividade', '>=', startOfDay),
                where('dataAtividade', '<=', endOfDay),
                orderBy('dataAtividade', 'desc')
            );

            const snapshot = await getDocs(qVisitas);
            let data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

            // Filtrar visitas canceladas (ROBUSTO)
            data = data.filter(v => !v.statusRegistro || v.statusRegistro.trim().toLowerCase() === 'ativo');

            setVisitas(data);
        } catch (error) {
            console.error("Erro ao buscar dados diários: ", error);
            alert("Erro ao buscar dados. Tente novamente.");
        } finally {
            setLoading(false);
        }
    };

    // ==============================
    // 🔹 Aplicar filtros
    // ==============================
    useEffect(() => {
        let result = visitas;

        if (filterAgente) {
            result = result.filter(v => {
                const agenteKey = v.idAgente || v.emailAgente || '';
                const nomeAgente = agentsMap[agenteKey] || agentsMap[agenteKey?.toLowerCase?.()] || agenteKey;
                return normalizarTexto(nomeAgente).includes(normalizarTexto(filterAgente));
            });
        }

        if (filterQuarteirao) {
            result = result.filter(v => v.quarteirao && v.quarteirao.toString().includes(filterQuarteirao));
        }

        if (filterLocalidade) {
            result = result.filter(v => v.localidade && normalizarTexto(v.localidade).includes(normalizarTexto(filterLocalidade)));
        }

        setFilteredVisitas(result);
    }, [visitas, filterAgente, filterQuarteirao, filterLocalidade, agentsMap]);

    // ==============================
    // 🔹 Funções do modal
    // ==============================
    const handleOpenModal = (visitaDaLinha) => {
        setSelectedVisit(visitaDaLinha);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedVisit(null);
    };

    const handleSaveSuccess = () => {
        handleCloseModal();
    };

    // ==============================
    // 🔹 Renderização
    // ==============================
    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3>Atendimentos Realizados Hoje ({new Date().toLocaleDateString('pt-BR')})</h3>
                <button
                    onClick={fetchVisitas}
                    disabled={loading}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.7 : 1
                    }}
                >
                    {loading ? 'Carregando...' : 'Atualizar Dados'}
                </button>
            </div>

            <div className="dashboard-summary">
                <div className="summary-card" style={{ maxHeight: '300px', overflowY: 'auto', padding: '15px' }}>
                    <h4 style={{ fontSize: '14px', marginBottom: '15px', marginTop: '5px' }}>Resumo por Localidade</h4>
                    {filteredVisitas.length === 0 ? (
                        <div style={{ fontSize: '14px' }}>0</div>
                    ) : (
                        <div style={{ textAlign: 'left', width: '100%', paddingTop: '5px' }}>
                            {Object.entries(filteredVisitas.reduce((acc, curr) => {
                                const loc = curr.localidade || 'Sem Localidade';
                                acc[loc] = (acc[loc] || 0) + 1;
                                return acc;
                            }, {})).sort((a, b) => b[1] - a[1]).map(([loc, count]) => (
                                <div key={loc} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px', paddingBottom: '2px', borderBottom: '1px solid #f0f0f0' }}>
                                    <div style={{ color: '#444' }}>{loc}:</div>
                                    <div style={{ fontWeight: 'bold' }}>{count}</div>
                                </div>
                            ))}
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: 'bold', marginTop: '8px', paddingTop: '6px', borderTop: '2px solid #ddd' }}>
                                <div>Total Geral:</div>
                                <div>{filteredVisitas.length}</div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="summary-card chart-card">
                    <h4>Visitas por Agente</h4>
                    {Object.keys(agentsMap).length > 0 ? (
                        <VisitasChart visitas={filteredVisitas} agentsMap={agentsMap} />
                    ) : (
                        <p>Carregando dados do gráfico...</p>
                    )}
                </div>
            </div>

            <div className="filters-container">
                <input
                    type="text"
                    placeholder="Filtrar por nome do agente..."
                    value={filterAgente}
                    onChange={(e) => setFilterAgente(e.target.value)}
                />
                <input
                    type="text"
                    placeholder="Filtrar por quarteirão..."
                    value={filterQuarteirao}
                    onChange={(e) => setFilterQuarteirao(e.target.value)}
                />
                <select
                    value={filterLocalidade}
                    onChange={(e) => setFilterLocalidade(e.target.value)}
                    style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                    <option value="">Todas as Localidades</option>
                    {OPCOES_LOCALIDADES.map(loc => (
                        <option key={loc} value={loc}>{loc}</option>
                    ))}
                </select>
            </div>

            {loading ? (
                <p>Carregando dados...</p>
            ) : (
                <div className="table-wrapper">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Horário</th>
                                <th>Agente</th>
                                <th>Localidade</th>
                                <th>Quarteirão</th>
                                <th>Nº</th>
                                <th>Comp.</th>
                                <th>Status</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredVisitas.map(v => {
                                const agenteKey = v.idAgente || v.emailAgente || '';
                                const nomeAgente = agentsMap[agenteKey] || agentsMap[agenteKey?.toLowerCase?.()] || agenteKey;
                                return (
                                    <tr key={v.id}>
                                        <td>{v.dataAtividade?.toDate?.().toLocaleTimeString('pt-BR') || '—'}</td>
                                        <td>{nomeAgente}</td>
                                        <td>{v.localidade || '—'}</td>
                                        <td>{v.quarteirao || '—'}</td>
                                        <td>{v.numeroImovel || 'N/A'}</td>
                                        <td>{v.complemento || '—'}</td>
                                        <td>
                                            <span className={`status status-${v.status?.replace(' ', '-').toLowerCase()}`}>
                                                {v.status || 'N/A'}
                                            </span>
                                        </td>
                                        <td>
                                            <button className="view-button" onClick={() => handleOpenModal(v)}>
                                                Visualizar
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {isModalOpen && (
                <EditModal
                    visita={selectedVisit}
                    agentsMap={agentsMap}
                    onClose={handleCloseModal}
                    onSaveSuccess={handleSaveSuccess}
                    currentUser={user}
                />
            )}
        </>
    );
}

export default DashboardDiario;